// Admin Page
import React, { useState, useEffect } from 'react';
import { 
  Settings, Users, Truck, Building, Tag, 
  ChevronRight, Plus, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { wacheApi, tagsApi } from '../lib/api';
import { useAuthStore, useWacheStore } from '../lib/store';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

function WacheSettingsTab() {
  const { wache, setWache } = useWacheStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    nummer: '',
    adresse: '',
  });

  useEffect(() => {
    if (wache) {
      setFormData({
        name: wache.name || '',
        nummer: wache.nummer || '',
        adresse: wache.adresse || '',
      });
    }
  }, [wache]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await wacheApi.update(formData);
      setWache(res.data);
      setIsEditing(false);
      toast.success('Wache-Einstellungen gespeichert');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Wache-Einstellungen
            </CardTitle>
            <CardDescription>Grundeinstellungen der Feuerwache</CardDescription>
          </div>
          {!isEditing && (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Bearbeiten
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Name der Wache</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
              disabled={!isEditing}
            />
          </div>
          <div className="space-y-2">
            <Label>Wachnummer</Label>
            <Input
              value={formData.nummer}
              onChange={(e) => setFormData(p => ({ ...p, nummer: e.target.value }))}
              disabled={!isEditing}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Adresse</Label>
          <Input
            value={formData.adresse}
            onChange={(e) => setFormData(p => ({ ...p, adresse: e.target.value }))}
            disabled={!isEditing}
            placeholder="Straße, PLZ Ort"
          />
        </div>
        
        {isEditing && (
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Speichere...' : 'Speichern'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AbteilungenTab() {
  const { abteilungen, setAbteilungen } = useWacheStore();
  const [isLoading, setIsLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAbtName, setNewAbtName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadAbteilungen();
  }, []);

  const loadAbteilungen = async () => {
    setIsLoading(true);
    try {
      const res = await wacheApi.getAbteilungen();
      setAbteilungen(res.data);
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAbtName.trim()) return;
    
    setIsCreating(true);
    try {
      await wacheApi.createAbteilung({ name: newAbtName });
      setNewAbtName('');
      setAddDialogOpen(false);
      loadAbteilungen();
      toast.success('Abteilung erstellt');
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Erstellen fehlgeschlagen');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Wachabteilungen
              </CardTitle>
              <CardDescription>Verwaltung der Wachabteilungen</CardDescription>
            </div>
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Abteilung hinzufügen
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (
            <div className="space-y-2">
              {abteilungen.map((abt, index) => (
                <div 
                  key={abt.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{index + 1}</Badge>
                    <span className="font-medium">{abt.name}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Abteilung</DialogTitle>
            <DialogDescription>Erstellen Sie eine neue Wachabteilung</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name der Abteilung</Label>
              <Input
                value={newAbtName}
                onChange={(e) => setNewAbtName(e.target.value)}
                placeholder="z.B. 5. Wachabteilung"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? 'Erstelle...' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TagsTab() {
  const { canSetTags } = useAuthStore();
  const canManage = canSetTags();
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', farbe: '#DC2626' });
  const [isCreating, setIsCreating] = useState(false);
  const [customColor, setCustomColor] = useState('');

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setIsLoading(true);
    try {
      const res = await tagsApi.getAll();
      setTags(res.data);
    } catch (error) {
      console.error('Load tags error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    
    setIsCreating(true);
    try {
      await tagsApi.create(formData);
      setFormData({ name: '', farbe: '#DC2626' });
      setCustomColor('');
      setAddDialogOpen(false);
      loadTags();
      toast.success('Tag erstellt');
    } catch (error) {
      console.error('Create tag error:', error);
      toast.error('Erstellen fehlgeschlagen');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (tagId) => {
    if (!confirm('Tag wirklich löschen?')) return;
    try {
      await tagsApi.deactivate(tagId);
      toast.success('Tag gelöscht');
      loadTags();
    } catch (error) {
      console.error('Delete tag error:', error);
      toast.error('Löschen fehlgeschlagen');
    }
  };

  // Expanded color palette - organized by hue
  const colorPalette = [
    // Reds
    { color: '#DC2626', name: 'Rot' },
    { color: '#EF4444', name: 'Hellrot' },
    { color: '#F87171', name: 'Rosa-Rot' },
    { color: '#B91C1C', name: 'Dunkelrot' },
    // Oranges
    { color: '#F97316', name: 'Orange' },
    { color: '#FB923C', name: 'Hell-Orange' },
    { color: '#EA580C', name: 'Dunkel-Orange' },
    // Yellows
    { color: '#F59E0B', name: 'Amber' },
    { color: '#FBBF24', name: 'Gelb' },
    { color: '#D97706', name: 'Dunkel-Amber' },
    // Greens
    { color: '#22C55E', name: 'Grün' },
    { color: '#10B981', name: 'Smaragd' },
    { color: '#14B8A6', name: 'Türkis' },
    { color: '#059669', name: 'Dunkelgrün' },
    { color: '#16A34A', name: 'Waldgrün' },
    // Blues
    { color: '#3B82F6', name: 'Blau' },
    { color: '#0EA5E9', name: 'Himmelblau' },
    { color: '#06B6D4', name: 'Cyan' },
    { color: '#2563EB', name: 'Dunkelblau' },
    { color: '#1D4ED8', name: 'Navy' },
    // Purples
    { color: '#8B5CF6', name: 'Violett' },
    { color: '#A855F7', name: 'Lila' },
    { color: '#7C3AED', name: 'Dunkel-Violett' },
    { color: '#C084FC', name: 'Hell-Lila' },
    // Pinks
    { color: '#EC4899', name: 'Pink' },
    { color: '#F472B6', name: 'Hell-Pink' },
    { color: '#DB2777', name: 'Magenta' },
    // Neutrals
    { color: '#6B7280', name: 'Grau' },
    { color: '#374151', name: 'Dunkelgrau' },
    { color: '#9CA3AF', name: 'Hellgrau' },
    { color: '#1F2937', name: 'Fast-Schwarz' },
  ];

  const handleCustomColorChange = (e) => {
    const value = e.target.value;
    setCustomColor(value);
    // Auto-validate hex color format
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      setFormData(p => ({ ...p, farbe: value }));
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Tags
              </CardTitle>
              <CardDescription>Tags zur Kategorisierung von Mitarbeitern</CardDescription>
            </div>
            {canManage && (
              <Button onClick={() => setAddDialogOpen(true)} data-testid="tag-hinzufuegen-btn">
                <Plus className="w-4 h-4 mr-2" />
                Tag hinzufügen
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex gap-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-20" />)}
            </div>
          ) : tags.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Noch keine Tags erstellt
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <Badge 
                  key={tag.id}
                  style={{ backgroundColor: tag.farbe }}
                  className="text-white group relative pr-8"
                >
                  {tag.name}
                  {canManage && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(tag.id); }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-white/20 rounded"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Neuer Tag</DialogTitle>
            <DialogDescription>Erstellen Sie einen neuen Tag</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="z.B. Maschinist, Atemschutz..."
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Farbe</Label>
                {/* Preview of selected color */}
                <div className="flex items-center gap-2">
                  <div 
                    className="w-6 h-6 rounded-full border-2 border-gray-300" 
                    style={{ backgroundColor: formData.farbe }}
                  />
                  <span className="text-xs font-mono text-muted-foreground">{formData.farbe}</span>
                </div>
              </div>
              
              {/* Color Grid */}
              <div className="grid grid-cols-8 gap-1.5 p-3 bg-muted/30 rounded-lg">
                {colorPalette.map(({ color, name }) => (
                  <button
                    key={color}
                    type="button"
                    title={name}
                    className={`w-7 h-7 rounded-full transition-all hover:scale-110 ${
                      formData.farbe === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => { setFormData(p => ({ ...p, farbe: color })); setCustomColor(''); }}
                  />
                ))}
              </div>
              
              {/* Custom color input */}
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <Input
                    type="text"
                    value={customColor}
                    onChange={handleCustomColorChange}
                    placeholder="#RRGGBB"
                    className="font-mono text-sm pl-9"
                  />
                  <input
                    type="color"
                    value={formData.farbe}
                    onChange={(e) => { setFormData(p => ({ ...p, farbe: e.target.value })); setCustomColor(e.target.value); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-5 h-5 p-0 border-0 cursor-pointer"
                    title="Farbwähler öffnen"
                  />
                </div>
                <span className="text-xs text-muted-foreground">oder Farbwähler nutzen</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? 'Erstelle...' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AdminPage() {
  const { user } = useAuthStore();

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold">Tags</h1>
          <p className="text-muted-foreground">
            Tags zur Kategorisierung von Mitarbeitern verwalten
          </p>
        </div>

        <TagsTab />
      </div>
    </MainLayout>
  );
}

export default AdminPage;
